/**
 * Inter-arrêts — liaisons A → B du référentiel (MBC phase 1).
 * Origine/Destination proviennent exclusivement de /api/stops.
 * Sélection d'une ligne → carte centrée sur les deux arrêts avec liaison.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Route, Plus, Pencil, Trash2, Power, RefreshCw, MapPin, ArrowRight, ArrowLeftRight, Download, FileSpreadsheet } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { downloadReferentialExport } from "@/lib/downloadReferential";
import { PageShell, PageHeader, SectionCard, ModernTable, SearchBar, ActionButton, StatusBadge, EmptyState, FilterTabs } from "@/components/ds";
import StopsReferentialMap from "@/components/stops/StopsReferentialMap";
import InterStopFormModal from "@/components/stops/InterStopFormModal";
import ConfirmDeleteDialog from "@/components/stops/ConfirmDeleteDialog";
import InterStopsImportDialog from "@/components/stops/InterStopsImportDialog";
import IconBtn from "@/components/stops/IconBtn";
import { formatKm, formatMinutes } from "@/lib/stopsUnits";

const COLUMNS = [
  { key: "origin", label: "Arrêt origine" },
  { key: "destination", label: "Arrêt destination" },
  { key: "distance", label: "Distance", align: "right" },
  { key: "time", label: "Temps de parcours", align: "right" },
  { key: "active", label: "Statut" },
  { key: "actions", label: "", align: "right" },
];

const STATUS_TABS = [
  { key: "all", label: "Tous" },
  { key: "active", label: "Actifs" },
  { key: "inactive", label: "Inactifs" },
];

export default function AdminInterStops() {
  const [stops, setStops] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState({ open: false, initial: null });
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, i] = await Promise.all([api.get("/stops"), api.get("/inter-stops")]);
      setStops(s.data?.items || []);
      setItems(i.data?.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase();
    return items.filter((r) => {
      if (tab === "active" && r.active === false) return false;
      if (tab === "inactive" && r.active !== false) return false;
      if (!n) return true;
      return `${r.origin_stop?.name || ""} ${r.destination_stop?.name || ""}`.toLowerCase().includes(n);
    });
  }, [items, q, tab]);

  const selected = useMemo(() => items.find((r) => r.id === selectedId) || null, [items, selectedId]);
  const link = selected?.origin_stop && selected?.destination_stop
    ? { origin: selected.origin_stop, destination: selected.destination_stop } : null;

  const upsertLocal = (doc) => setItems((prev) => (prev.some((r) => r.id === doc.id)
    ? prev.map((r) => (r.id === doc.id ? doc : r)) : [doc, ...prev]));

  const submitForm = async (payload) => {
    setSaving(true);
    try {
      if (form.initial?.id) {
        const r = await api.patch(`/inter-stops/${form.initial.id}`, payload);
        upsertLocal(r.data); toast.success("Inter-arrêt modifié");
      } else {
        const r = await api.post("/inter-stops", payload);
        upsertLocal(r.data); setSelectedId(r.data.id); toast.success("Inter-arrêt créé");
      }
      setForm({ open: false, initial: null });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const toggleActive = async (row) => {
    try {
      const r = await api.patch(`/inter-stops/${row.id}`, { active: !row.active });
      upsertLocal(r.data); toast.success(r.data.active ? "Inter-arrêt réactivé" : "Inter-arrêt désactivé");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/inter-stops/${toDelete.id}`);
      setItems((prev) => prev.filter((r) => r.id !== toDelete.id));
      if (selectedId === toDelete.id) setSelectedId(null);
      toast.success("Inter-arrêt supprimé"); setToDelete(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail)); setToDelete(null);
    } finally { setDeleting(false); }
  };

  const hasReverse = (row) => items.some((r) => r.origin_stop_id === row.destination_stop_id && r.destination_stop_id === row.origin_stop_id);
  const openReverse = (row) => setForm({ open: true, initial: {
    origin_stop_id: row.destination_stop_id, destination_stop_id: row.origin_stop_id,
    distance_meters: row.distance_meters, travel_time_seconds: row.travel_time_seconds,
    reverse_of: `${row.origin_stop?.name} → ${row.destination_stop?.name}`,
  } });

  const renderCell = (row, col) => {
    switch (col.key) {
      case "origin":
        return <span className="font-bold" data-testid={`inter-row-origin-${row.id}`}>{row.origin_stop?.name || <em className="text-red-600 font-normal">arrêt supprimé</em>}</span>;
      case "destination":
        return <span className="font-bold" data-testid={`inter-row-destination-${row.id}`}>{row.destination_stop?.name || <em className="text-red-600 font-normal">arrêt supprimé</em>}</span>;
      case "distance":
        return <span className="font-mono" data-testid={`inter-row-distance-${row.id}`}>{formatKm(row.distance_meters)}</span>;
      case "time":
        return <span className="font-mono" data-testid={`inter-row-time-${row.id}`}>{formatMinutes(row.travel_time_seconds)}</span>;
      case "active":
        return <StatusBadge tone={row.active ? "success" : "neutral"} label={row.active ? "Actif" : "Inactif"} />;
      case "actions":
        return (
          <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
            <IconBtn icon={ArrowLeftRight} title={hasReverse(row) ? "Liaison inverse déjà existante" : "Créer la liaison inverse B→A (à valider)"} testId={`inter-reverse-${row.id}`} onClick={() => !hasReverse(row) && openReverse(row)} tone={hasReverse(row) ? "muted" : "default"} />
            <IconBtn icon={Pencil} title="Modifier" testId={`inter-edit-${row.id}`} onClick={() => setForm({ open: true, initial: row })} />
            <IconBtn icon={Power} title={row.active ? "Désactiver" : "Réactiver"} testId={`inter-toggle-${row.id}`} onClick={() => toggleActive(row)} tone={row.active ? "warn" : "ok"} />
            <IconBtn icon={Trash2} title="Supprimer" testId={`inter-delete-${row.id}`} onClick={() => setToDelete(row)} tone="danger" />
          </div>
        );
      default:
        return row[col.key];
    }
  };

  return (
    <PageShell testId="admin-inter-stops-page">
      <PageHeader
        eyebrow="Planification"
        title="Inter-arrêts"
        subtitle="Liaisons origine → destination entre arrêts du référentiel, avec distance et temps de parcours saisis manuellement."
        actions={
          <>
            <ActionButton variant="ghost" icon={RefreshCw} onClick={load} loading={loading} testId="inter-stops-refresh">Actualiser</ActionButton>
            <ActionButton variant="ghost" icon={MapPin} to="/admin/stops" testId="inter-stops-goto-stops">Arrêts</ActionButton>
            <ActionButton variant="ghost" icon={Download} onClick={downloadReferentialExport} testId="inter-stops-export-btn">Exporter Excel</ActionButton>
            <ActionButton variant="ghost" icon={FileSpreadsheet} onClick={() => setImportOpen(true)} testId="inter-stops-import-btn">Importer Excel</ActionButton>
            <ActionButton variant="primary" icon={Plus} onClick={() => setForm({ open: true, initial: null })} testId="inter-stops-add-btn" disabled={stops.filter((s) => s.active !== false).length < 2}>
              Ajouter un inter-arrêt
            </ActionButton>
          </>
        }
      />

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
        <SectionCard title="Liste des inter-arrêts" icon={Route} tone="white" className="xl:col-span-3"
          actions={<span className="text-[10px] font-mono text-zinc-500" data-testid="inter-stops-count">{filtered.length}/{items.length}</span>}>
          <div className="p-3 border-b border-zinc-200 flex flex-wrap gap-2 items-center">
            <SearchBar value={q} onChange={setQ} placeholder="Rechercher par arrêt origine ou destination…" testId="inter-stops-search" dense className="flex-1 min-w-[220px]" />
            <FilterTabs options={STATUS_TABS} value={tab} onChange={setTab} testIdPrefix="inter-stops-filter" />
          </div>
          {!loading && items.length === 0 ? (
            <EmptyState icon={Route} title="Aucun inter-arrêt"
              description={stops.length < 2 ? "Créez d'abord au moins deux arrêts dans le référentiel Arrêts." : "Ajoutez une première liaison A → B."}
              action={stops.length < 2
                ? <ActionButton variant="ghost" icon={MapPin} to="/admin/stops" testId="inter-stops-empty-goto-stops">Aller aux Arrêts</ActionButton>
                : <ActionButton variant="primary" icon={Plus} onClick={() => setForm({ open: true, initial: null })} testId="inter-stops-empty-add">Ajouter un inter-arrêt</ActionButton>} />
          ) : (
            <div className="max-h-[520px] overflow-auto">
              <ModernTable
                columns={COLUMNS} rows={filtered} renderCell={renderCell} dense stickyHeader
                testId="inter-stops-table" bodyTestId="inter-stops-table-body"
                emptyMessage="Aucun inter-arrêt ne correspond aux filtres."
                onRowClick={(row) => setSelectedId(row.id)}
                rowClassName={(row) => (row.id === selectedId ? "bg-[#FFF7CC]" : "")}
                className="border-0"
              />
            </div>
          )}
        </SectionCard>

        <SectionCard title="Carte de la liaison" icon={MapPin} tone="navy" className="xl:col-span-2" bodyClassName="h-[560px] flex flex-col"
          actions={selected && (
            <span className="text-[10px] font-mono flex items-center gap-1" data-testid="inter-stops-map-selected">
              {selected.origin_stop?.name} <ArrowRight size={10} /> {selected.destination_stop?.name}
            </span>
          )}>
          {selected && (
            <div className="px-3 py-2 border-b border-zinc-200 bg-zinc-50 text-xs flex flex-wrap gap-x-4 gap-y-1" data-testid="inter-stops-selected-summary">
              <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-600 border border-zinc-900 mr-1" />A · {selected.origin_stop?.name}</span>
              <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-red-600 border border-zinc-900 mr-1" />B · {selected.destination_stop?.name}</span>
              <span className="font-mono">{formatKm(selected.distance_meters)} · {formatMinutes(selected.travel_time_seconds)}</span>
            </div>
          )}
          <div className="flex-1 min-h-0">
            <StopsReferentialMap
              stops={link ? [link.origin, link.destination] : stops}
              link={link}
              testId="inter-stops-map"
            />
          </div>
          {!selected && (
            <div className="px-3 py-2 border-t border-zinc-200 text-[11px] text-zinc-500" data-testid="inter-stops-map-hint">
              Sélectionnez un inter-arrêt dans la liste pour visualiser la liaison. La ligne affichée est une liaison schématique, pas un tracé routier.
            </div>
          )}
        </SectionCard>
      </div>

      <InterStopsImportDialog open={importOpen} onClose={() => setImportOpen(false)} onImported={load} />
      <InterStopFormModal open={form.open} initial={form.initial} stops={stops} saving={saving}
        onClose={() => setForm({ open: false, initial: null })} onSubmit={submitForm} />
      <ConfirmDeleteDialog
        open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)} loading={deleting} onConfirm={confirmDelete}
        title="Supprimer cet inter-arrêt ?"
        description={toDelete ? `La liaison « ${toDelete.origin_stop?.name || "?"} → ${toDelete.destination_stop?.name || "?"} » sera supprimée. Les arrêts eux-mêmes ne sont pas touchés.` : ""}
        testIdPrefix="inter-delete"
      />
    </PageShell>
  );
}
